import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '@/lib/apiClient'

// Fase R.11 (Bloque F-M, 17/08/2026): Mi Banca Personal -- contabilidad NUEVA e
// independiente de MAIN_PAPER/PaperTrade/Shadow/Fun Bet. Nunca compartir queryKey con
// `useBankrollAccounts` (la banca ADMIN histórica, ver `BankrollPage.tsx`/`useBankroll.ts`).

// P0.23 (22/08/2026, Sección 15/32 del encargo): forma REAL de
// `PersonalBankrollService.compute_metrics()` (P0.21, `app/personal_bankroll/service.py`) --
// `profit_loss` NUNCA existió en la respuesta real (se llamaba `betting_profit_loss` desde el
// principio); el hook lo tipaba mal y la UI mostraba "—" para siempre. `net_contributions`/
// `total_deposits`/`total_withdrawals` vienen del ledger real, separados a propósito de
// `betting_profit_loss` (Sección 3: un depósito nunca es beneficio, un retiro nunca es pérdida).
export interface PersonalBankrollMetrics {
  configured: boolean
  cycle?: number
  is_current_cycle?: boolean
  initial_capital?: number | null
  current_balance?: number | null
  net_contributions?: number
  total_deposits?: number
  total_withdrawals?: number
  betting_profit_loss?: number
  win_rate?: number | null
  roi?: number | null
  yield_pct?: number | null
  total_staked?: number
  won?: number
  lost?: number
  void?: number
  pending?: number
  average_odds_played?: number | null
}

export function usePersonalBankroll(days?: number, cycle?: number) {
  return useQuery<PersonalBankrollMetrics>({
    queryKey: ['mini-app', 'personal-bankroll', days ?? 'total', cycle ?? 'current'],
    queryFn: async () =>
      (await apiClient.get('/api/mini-app/bankroll/personal', { params: { ...(days ? { days } : {}), ...(cycle ? { cycle } : {}) } })).data,
    staleTime: 15_000,
  })
}

export function useSetupPersonalBankroll() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (initialCapital: number) =>
      (await apiClient.post('/api/mini-app/bankroll/personal/setup', { initial_capital: initialCapital })).data,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['mini-app', 'personal-bankroll'] })
    },
  })
}

export interface PersonalBankrollTransaction {
  id: number
  transaction_type: string
  amount: number
  balance_before: number
  balance_after: number
  notes: string | null
  created_at: string
}

export function usePersonalBankrollHistory(cycle?: number) {
  return useQuery<PersonalBankrollTransaction[]>({
    queryKey: ['mini-app', 'personal-bankroll-history', cycle ?? 'current'],
    queryFn: async () =>
      (await apiClient.get('/api/mini-app/bankroll/personal/history', { params: cycle ? { cycle } : {} })).data,
    staleTime: 15_000,
  })
}

export function usePersonalBankrollCycles() {
  return useQuery<{ cycles: number[]; current_cycle: number | null }>({
    queryKey: ['mini-app', 'personal-bankroll-cycles'],
    queryFn: async () => (await apiClient.get('/api/mini-app/bankroll/personal/cycles')).data,
    staleTime: 60_000,
  })
}

interface MovementPayload {
  amount: number
  idempotency_key?: string
  notes?: string
}

/** Sección 16 del encargo: depositar/retirar es SOLO contabilidad dentro de John Anthony,
 * nunca mueve dinero bancario real -- la UI que llama a estos mutations debe explicarlo. */
export function useDepositPersonalBankroll() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (payload: MovementPayload) =>
      (await apiClient.post('/api/mini-app/bankroll/personal/deposit', payload)).data,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['mini-app', 'personal-bankroll'] })
      queryClient.invalidateQueries({ queryKey: ['mini-app', 'personal-bankroll-history'] })
    },
  })
}

export function useWithdrawPersonalBankroll() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (payload: MovementPayload) =>
      (await apiClient.post('/api/mini-app/bankroll/personal/withdraw', payload)).data,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['mini-app', 'personal-bankroll'] })
      queryClient.invalidateQueries({ queryKey: ['mini-app', 'personal-bankroll-history'] })
    },
  })
}

export function useAdjustPersonalBankroll() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (payload: { amount: number; reason: string }) =>
      (await apiClient.post('/api/mini-app/bankroll/personal/adjust', payload)).data,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['mini-app', 'personal-bankroll'] })
      queryClient.invalidateQueries({ queryKey: ['mini-app', 'personal-bankroll-history'] })
    },
  })
}

export function useResetPersonalBankroll() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (reason?: string) =>
      (await apiClient.post('/api/mini-app/bankroll/personal/reset', { reason })).data,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['mini-app', 'personal-bankroll'] })
      queryClient.invalidateQueries({ queryKey: ['mini-app', 'personal-bankroll-history'] })
      queryClient.invalidateQueries({ queryKey: ['mini-app', 'personal-bankroll-cycles'] })
    },
  })
}

export interface PersonalBet {
  id: number
  decision_record_id: number | null
  challenge_step_id: number | null
  parlay_id?: number | null
  exact_score_id?: number | null
  ja_lab_usage_id?: number | null
  parlay_lab_run_id?: number | null
  sgp_lab_run_id?: number | null
  fixture_id: number | null
  run_type: 'run1' | 'run2' | null
  source: 'RECOMMENDATION' | 'CHALLENGE'
  category: string
  market: string
  selection: string
  // Sprint "Humanización central de mercados" (03/09/2026): el backend ya humaniza
  // (`MarketDisplayFormatter`, única fuente) -- el cliente nunca vuelve a traducir
  // `market`/`selection` crudos por su cuenta (Regla 2, mismo criterio que el resto de la
  // Mini App). `market`/`selection` se conservan por compatibilidad/depuración, nunca para
  // renderizar directamente.
  market_label: string
  selection_label: string
  match_label: string | null
  // Sprint "Marcadores Probables V2" (05/09/2026, Sección 3 del encargo): `null` real --
  // "John Anthony no tenía cuota real para esto" (típico de un Marcador Probable), nunca un
  // 0.0 fabricado. `formatOdds()` ya maneja `null` mostrando "—".
  reference_odds: number | null
  played_odds: number
  bookmaker: string | null
  stake: number
  bet_date: string
  status: 'PENDING' | 'WON' | 'LOST' | 'VOID'
  profit_loss: number | null
  balance_after: number | null
  settled_at: string | null
  created_at: string
  /** P0.55 (Decision Journal BLACK) -- nota opcional del propio dueño de la apuesta. */
  notes: string | null
}

export function useMyBets(window: '7D' | '30D' | '90D' | 'TOTAL' = 'TOTAL') {
  return useQuery<{ window: string; items: PersonalBet[] }>({
    queryKey: ['mini-app', 'my-bets', window],
    queryFn: async () => (await apiClient.get('/api/mini-app/bets', { params: { window } })).data,
    staleTime: 15_000,
  })
}

export interface RegisterBetPayload {
  decision_record_id?: number
  challenge_step_id?: number
  parlay_id?: number
  // Sprint "Mi Banca V2" (10/09/2026, Sección 1/2 del encargo): 3 orígenes nuevos --
  // exactamente uno de los 6 debe venir informado (mismo criterio que el backend).
  ja_lab_usage_id?: number
  parlay_lab_run_id?: number
  sgp_lab_run_id?: number
  // Solo se usa (y se exige) junto a `ja_lab_usage_id` -- desambigua CUÁL de las hasta 3
  // recomendaciones reales de esa ejecución se está jugando.
  market?: string
  category?: string
  played_odds: number
  stake: number
  // Opcional, nunca obligatorio -- dónde consiguió el usuario su cuota real.
  bookmaker?: string
}

export function useRegisterBet() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (payload: RegisterBetPayload) => (await apiClient.post('/api/mini-app/bets', payload)).data,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['mini-app', 'my-bets'] })
      queryClient.invalidateQueries({ queryKey: ['mini-app', 'personal-bankroll'] })
    },
  })
}

/** P0.55 (Decision Journal BLACK, Sección 18/19 del encargo) -- nota opcional sobre una
 * apuesta propia ya registrada. Nunca obligatoria. */
export function useUpdateBetNotes() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ betId, notes }: { betId: number; notes: string | null }) =>
      (await apiClient.patch(`/api/mini-app/bets/${betId}/notes`, { notes })).data,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['mini-app', 'my-bets'] })
    },
  })
}
